/**
 * Shared Framer-Motion spring configuration and reduced-motion helpers.
 * Import `springTransition` and `motionSafe` throughout the app so every
 * animation lives on the same spring curve and degrades correctly when
 * `prefers-reduced-motion` is active.
 */
import { useReducedMotion } from 'framer-motion';
import type { Transition, Variants } from 'framer-motion';

// ── Spring definition ────────────────────────────────────────────────────────

/**
 * The single shared spring used everywhere in the Abyss motion kit.
 * Stiffness 240 / damping 22 gives a quick, confident snap with just
 * enough oscillation to feel alive without bouncing.
 */
export const springTransition: Transition = {
  type: 'spring',
  stiffness: 240,
  damping: 22,
};

/**
 * Instant (zero-duration) transition — substituted when the user has
 * `prefers-reduced-motion: reduce` active.
 */
export const instantTransition: Transition = {
  type: 'tween',
  duration: 0,
};

// ── Reduced-motion hook ──────────────────────────────────────────────────────

/**
 * Returns `'reduced'` when `prefers-reduced-motion: reduce` is active,
 * `'full'` otherwise. Components should switch to instant transitions or
 * skip decorative animations entirely when this returns `'reduced'`.
 *
 * @example
 * const pref = useMotionPreference();
 * const transition = pref === 'full' ? springTransition : instantTransition;
 */
export function useMotionPreference(): 'full' | 'reduced' {
  const shouldReduce = useReducedMotion();
  return shouldReduce ? 'reduced' : 'full';
}

// ── Variants helper ──────────────────────────────────────────────────────────

/**
 * Wraps a `Variants` map so that when motion is reduced, every variant
 * collapses to its `visible`/`enter` state immediately (zero transition).
 *
 * Usage:
 * ```tsx
 * const pref = useMotionPreference();
 * const vars = motionSafe(pref, {
 *   hidden: { opacity: 0, y: 12 },
 *   visible: { opacity: 1, y: 0 },
 * });
 * <motion.div variants={vars} initial="hidden" animate="visible" />
 * ```
 */
export function motionSafe(
  preference: 'full' | 'reduced',
  variants: Variants,
): Variants {
  if (preference === 'full') return variants;

  // When reduced: collapse everything to the "visible/enter" target instantly.
  const resolved: Variants = {};
  for (const [key, def] of Object.entries(variants)) {
    resolved[key] = {
      ...(typeof def === 'object' && def !== null ? def : {}),
      transition: instantTransition,
    };
  }
  return resolved;
}
